/**
 * Upgrades a set of <select> elements into accessible custom dropdowns that
 * share "only one open at a time" behavior. Used by the homepage device
 * finder (custom-product-finder.liquid) and the in-collection device filter
 * picker (collection-device-filter.liquid).
 *
 * Each dropdown is an ARIA select-only combobox: the button keeps focus and
 * points at the highlighted option with aria-activedescendant. The native
 * <select> stays as the value store for the section scripts, hidden from
 * assistive technology and the Tab order.
 *
 * Expects each select's markup (see either section for the canonical shape):
 *   .finder__select-wrap
 *     label[for=<select id>]  (visually hidden; names the combobox)
 *     select.finder__select
 *     button.finder__select-button > span.finder__select-text
 *     ul.finder__options
 *
 * @param {HTMLSelectElement[]} selects - Selects that close each other when one opens.
 * @param {Element} [container] - Outside click on this element closes all selects.
 */
function createProgressiveSelectGroup(selects, container) {
  // The select that should receive focus once it is populated, because the
  // shopper just picked a value in the step before it.
  var pendingFocus = null;
  var lastKeyTime = 0;

  function partsOf(wrap) {
    return {
      button: wrap.querySelector('.finder__select-button'),
      list: wrap.querySelector('.finder__options')
    };
  }

  function optionsOf(list) {
    return Array.prototype.slice.call(list.querySelectorAll('[role="option"]'));
  }

  function closeWrap(wrap) {
    var parts = partsOf(wrap);
    wrap.classList.remove('is-open');
    if (parts.button) {
      parts.button.setAttribute('aria-expanded', 'false');
      parts.button.removeAttribute('aria-activedescendant');
    }
    if (parts.list) parts.list.hidden = true;
  }

  function closeAll(except) {
    selects.forEach(function (select) {
      var wrap = select.closest('.finder__select-wrap');
      if (!wrap || wrap === except) return;
      closeWrap(wrap);
    });
  }

  function activeIndex(wrap) {
    var parts = partsOf(wrap);
    var id = parts.button.getAttribute('aria-activedescendant');
    return optionsOf(parts.list).findIndex(function (item) { return item.id === id; });
  }

  function setActive(wrap, index) {
    var parts = partsOf(wrap);
    var items = optionsOf(parts.list);
    if (!items.length) return;
    index = Math.max(0, Math.min(items.length - 1, index));
    items.forEach(function (item, i) {
      item.classList.toggle('is-active', i === index);
    });
    var item = items[index];
    parts.button.setAttribute('aria-activedescendant', item.id);

    // Scroll the list only, never the page.
    var list = parts.list;
    if (item.offsetTop < list.scrollTop) {
      list.scrollTop = item.offsetTop;
    } else if (item.offsetTop + item.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = item.offsetTop + item.offsetHeight - list.clientHeight;
    }
  }

  function openWrap(wrap, index) {
    var parts = partsOf(wrap);
    if (parts.button.disabled) return;
    closeAll(wrap);
    wrap.classList.add('is-open');
    parts.button.setAttribute('aria-expanded', 'true');
    parts.list.hidden = false;
    if (index === undefined) {
      index = Math.max(0, optionsOf(parts.list).findIndex(function (item) {
        return item.getAttribute('aria-selected') === 'true';
      }));
    }
    setActive(wrap, index);
  }

  function commit(select, item) {
    closeWrap(select.closest('.finder__select-wrap'));
    if (!item) return;
    pendingFocus = selects[selects.indexOf(select) + 1] || null;
    select.value = item.dataset.value;
    syncSelectUi(select);
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Move focus to the next step once it has options, unless the shopper has
  // already moved focus somewhere else while it was loading.
  function focusIfPending(select) {
    if (select !== pendingFocus || select.disabled) return;
    pendingFocus = null;
    var previous = selects[selects.indexOf(select) - 1];
    var previousButton = previous && partsOf(previous.closest('.finder__select-wrap')).button;
    var active = document.activeElement;
    if (active && active !== document.body && active !== previousButton) return;
    var button = partsOf(select.closest('.finder__select-wrap')).button;
    if (button && !button.disabled) button.focus();
  }

  function typeahead(wrap, key) {
    var items = optionsOf(partsOf(wrap).list);
    var now = Date.now();
    var text = (now - (wrap.typeaheadTime || 0) < 600 ? wrap.typeaheadText || '' : '') + key.toLowerCase();
    wrap.typeaheadText = text;
    wrap.typeaheadTime = now;
    // A single repeated letter cycles; a longer string refines from the current option.
    var start = activeIndex(wrap) + (text.length === 1 ? 1 : 0);
    for (var n = 0; n < items.length; n += 1) {
      var i = (start + n + items.length) % items.length;
      if (items[i].textContent.trim().toLowerCase().indexOf(text) === 0) {
        if (wrap.classList.contains('is-open')) setActive(wrap, i);
        else openWrap(wrap, i);
        return;
      }
    }
  }

  function syncSelectUi(select) {
    var wrap = select.closest('.finder__select-wrap');
    if (!wrap) return;

    var button = wrap.querySelector('.finder__select-button');
    var text = wrap.querySelector('.finder__select-text');
    var optionsList = wrap.querySelector('.finder__options');
    if (!button || !text || !optionsList) return;

    var selectedOption = select.options[select.selectedIndex] || select.options[0];
    text.textContent = selectedOption ? selectedOption.text : '';
    optionsList.innerHTML = '';

    Array.prototype.forEach.call(select.options, function (option, index) {
      if (option.value === '') return;

      var item = document.createElement('li');
      var selected = option.value === select.value;
      item.id = optionsList.id + '-' + index;
      item.className = 'finder__option';
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', selected ? 'true' : 'false');
      item.textContent = option.text;
      item.dataset.value = option.value;

      if (selected) {
        item.classList.add('is-selected');
      }

      optionsList.appendChild(item);
    });

    button.disabled = select.disabled || optionsList.children.length === 0;
    if (button.disabled) closeAll();
  }

  function setPlaceholder(select, label) {
    select.innerHTML = '';
    select.add(new Option(label, ''));
    syncSelectUi(select);
  }

  function setLoading(select, label) {
    select.disabled = true;
    setPlaceholder(select, label);
  }

  function setError(select, label) {
    select.disabled = true;
    setPlaceholder(select, label);
  }

  function resetSelect(select, label, disabled) {
    setPlaceholder(select, label);
    select.disabled = disabled;
    syncSelectUi(select);
  }

  /**
   * @param {HTMLSelectElement} select
   * @param {string} label - Placeholder option text.
   * @param {Array} items - Raw items to populate from.
   * @param {function(*, number): {text: string, value: string, dataset?: object}} mapItem
   */
  function populate(select, label, items, mapItem) {
    setPlaceholder(select, label);

    items.forEach(function (item, index) {
      var mapped = mapItem(item, index);
      var option = new Option(mapped.text, mapped.value);
      if (mapped.dataset) {
        Object.keys(mapped.dataset).forEach(function (key) {
          option.dataset[key] = mapped.dataset[key];
        });
      }
      select.add(option);
    });

    select.disabled = items.length === 0;
    syncSelectUi(select);
    focusIfPending(select);
  }

  function handleKeydown(event, select, wrap) {
    var open = wrap.classList.contains('is-open');
    var items = optionsOf(partsOf(wrap).list);
    var index = activeIndex(wrap);

    switch (event.key) {
      case 'ArrowDown':
        if (!open) openWrap(wrap);
        else setActive(wrap, index + 1);
        break;
      case 'ArrowUp':
        if (!open) openWrap(wrap);
        else if (event.altKey) commit(select, items[index]);
        else setActive(wrap, index - 1);
        break;
      case 'Home':
        if (open) setActive(wrap, 0);
        else openWrap(wrap, 0);
        break;
      case 'End':
        if (open) setActive(wrap, items.length - 1);
        else openWrap(wrap, items.length - 1);
        break;
      case 'PageDown':
      case 'PageUp':
        if (!open) return;
        setActive(wrap, index + (event.key === 'PageDown' ? 10 : -10));
        break;
      case 'Enter':
      case ' ':
        lastKeyTime = Date.now();
        if (event.key === ' ' && open && lastKeyTime - (wrap.typeaheadTime || 0) < 600) typeahead(wrap, ' ');
        else if (open) commit(select, items[index]);
        else openWrap(wrap);
        break;
      case 'Escape':
        if (!open) return;
        closeWrap(wrap);
        break;
      case 'Tab':
        if (open) closeWrap(wrap);
        return;
      default:
        if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return;
        typeahead(wrap, event.key);
    }

    event.preventDefault();
  }

  function setupOne(select) {
    var wrap = select.closest('.finder__select-wrap');
    if (!wrap) return;

    var button = wrap.querySelector('.finder__select-button');
    var optionsList = wrap.querySelector('.finder__options');
    if (!button || !optionsList) return;

    var label = wrap.querySelector('label[for="' + select.id + '"]');
    optionsList.id = select.id + '-listbox';
    optionsList.setAttribute('role', 'listbox');
    button.setAttribute('role', 'combobox');
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-controls', optionsList.id);
    if (label) {
      label.id = label.id || select.id + '-label';
      button.setAttribute('aria-labelledby', label.id);
      optionsList.setAttribute('aria-labelledby', label.id);
    }
    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');

    button.addEventListener('click', function (event) {
      event.preventDefault();
      // Enter/Space are handled on keydown; ignore the click they synthesize.
      if (event.detail === 0 && Date.now() - lastKeyTime < 400) return;
      if (button.disabled) return;

      if (wrap.classList.contains('is-open')) {
        closeWrap(wrap);
      } else {
        openWrap(wrap);
        // A pointer open keeps the old look: no highlight until one is picked.
        if (event.detail > 0 && !select.value) {
          optionsOf(optionsList).forEach(function (item) { item.classList.remove('is-active'); });
          button.removeAttribute('aria-activedescendant');
        }
      }
    });

    button.addEventListener('keydown', function (event) {
      handleKeydown(event, select, wrap);
    });

    button.addEventListener('blur', function () {
      closeWrap(wrap);
    });

    // Keep focus on the combobox while the pointer picks an option.
    optionsList.addEventListener('mousedown', function (event) {
      event.preventDefault();
    });

    optionsList.addEventListener('click', function (event) {
      var item = event.target.closest('[role="option"]');
      if (item) commit(select, item);
    });

    select.addEventListener('change', function () {
      syncSelectUi(select);
    });

    syncSelectUi(select);
  }

  selects.forEach(setupOne);

  document.addEventListener('click', function (event) {
    if (container && !container.contains(event.target)) closeAll();
  });
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeAll();
  });

  return {
    syncSelectUi: syncSelectUi,
    closeAll: closeAll,
    setPlaceholder: setPlaceholder,
    setLoading: setLoading,
    setError: setError,
    resetSelect: resetSelect,
    populate: populate
  };
}

window.createProgressiveSelectGroup = createProgressiveSelectGroup;
